/**
 * QSCallCTA — reusable upsell card.
 *
 * Customer-facing label is "Project Cost Review" (per the Apr-2026 brand
 * refresh). All INTERNAL references — variable names, data-testids, route
 * paths (/billing/qs-call-checkout, /billing/qs-call-redeem, /billing/me),
 * Stripe product names, admin labels and database fields — remain unchanged
 * as `qs_call` / `qs_check` so we don't churn the integration layer.
 *
 * Used in 4 places:
 *   1. Indicative output screen State B (below QS Check upsell)
 *   2. SCOPE success page (below QS Check card)
 *   3. Client portal sidebar
 *   4. Post-QS-Check delivery email (rendered server-side; this is just the UI version)
 *
 * When clicked:
 *   - Anonymous / non-subscriber users → POST /api/billing/qs-call-checkout → Stripe £49
 *   - Pro / Pro+ users with allowance remaining → see the inline redeem badge + button
 *     (they get the same card plus a small "Redeem free this month" pill above the CTA)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QSCallCTA = ({
  variant = 'card', // 'card' | 'compact'
  userEmail = '',
  showRedeem = false, // when true, tries /billing/me and shows redeem pill if Pro+ with allowance
  testid = 'qs-call-cta',
  draftId = null, // optional — links the call to a specific scope draft so the surveyor sees the project
}) => {
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!showRedeem) return;
    axios.get(`${API}/billing/me`)
      .then((r) => setBilling(r.data))
      .catch(() => setBilling(null));
  }, [showRedeem]);

  const startPaidCheckout = async () => {
    setLoading(true);
    try {
      const body = {};
      if (userEmail) body.email = userEmail;
      if (draftId) body.draft_id = draftId;
      const res = await axios.post(`${API}/billing/qs-call-checkout`, body);
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      setLoading(false);
      // Silent fallback — user can retry
    }
  };

  const redeem = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/billing/qs-call-redeem`, {});
      if (res.data?.calendly_url) {
        window.open(res.data.calendly_url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      setLoading(false);
    }
  };

  const callsRemaining = billing?.monthly_allowances?.quarterly_calls_remaining
    ?? billing?.monthly_allowances?.qs_calls_remaining ?? 0;
  const callsCap = billing?.monthly_allowances?.quarterly_calls_cap
    ?? billing?.monthly_allowances?.qs_calls_cap ?? 0;
  const isSubscriber = billing?.tier === 'pro' || billing?.tier === 'pro_plus';
  const isSubscriberWithAllowance = isSubscriber && callsRemaining > 0;
  const isSubscriberExhausted = isSubscriber && callsCap > 0 && callsRemaining === 0;
  const tierLabel = billing?.tier === 'pro_plus' ? 'Pro+' : 'Pro';
  const resetOn = billing?.monthly_allowances?.reset_on
    ? new Date(billing.monthly_allowances.reset_on).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  if (variant === 'compact') {
    return (
      <button
        onClick={isSubscriberWithAllowance ? redeem : startPaidCheckout}
        disabled={loading}
        data-testid={testid}
        style={{
          background: '#2563EB', color: '#fff', border: 'none',
          padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
          cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1,
          display: 'inline-flex', alignItems: 'center', gap: 8,
        }}
      >
        <Phone size={14} />
        {loading ? 'Starting…'
          : isSubscriberWithAllowance ? `Redeem your QS Review Call (${tierLabel})`
          : 'Book a QS Review Call — £49'}
      </button>
    );
  }

  return (
    <div>
      {isSubscriberExhausted && (
        <div
          data-testid={`${testid}-exhausted`}
          style={{
            background: '#FEF3C7', border: '1.5px solid #FDE68A',
            borderRadius: 10, padding: '10px 14px', marginBottom: 10,
            fontSize: 12, color: '#92400E',
          }}
        >
          You've used all your QS Review Calls this month.{resetOn && ` Your allowance resets on ${resetOn}.`}
          {' '}You can still book a paid call below.
        </div>
      )}
      <div
        data-testid={testid}
        style={{
          background: '#0F2742',
          borderRadius: 14,
          padding: '24px 26px',
          color: '#fff',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em',
                      textTransform: 'uppercase', color: '#93C5FD', marginBottom: 8 }}>
          Professional QS Services
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: '#1E40AF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Phone size={20} style={{ color: '#fff' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
              QS Review Call — 20 minutes
            </div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,.78)', lineHeight: 1.55, margin: '0 0 8px' }}>
              A 20-minute call with a chartered Quantity Surveyor to review the rates assumed in your spec, advise on finding a builder, comparing tenders and setting up the right contract. Book a slot via Calendly after payment.
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,.62)', lineHeight: 1.55, margin: '0 0 8px' }}>
              <em>Verbal advice only — not project management or unlimited follow-up support.</em>
            </p>
            <p style={{ fontSize: 13, color: '#fff', fontWeight: 600, margin: '0 0 14px' }}>
              Cost clarity before you commit.
            </p>
            {isSubscriberWithAllowance && (
              <div
                data-testid={`${testid}-redeem-hint`}
                style={{
                  display: 'inline-block',
                  background: 'rgba(255,255,255,.1)',
                  border: '1px solid rgba(255,255,255,.25)',
                  color: '#BFDBFE',
                  fontSize: 11, fontWeight: 700, letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  padding: '4px 10px', borderRadius: 999, marginBottom: 12,
                }}
              >
                {tierLabel} — {callsRemaining} of {callsCap} calls remaining this month
              </div>
            )}
            <div>
              <button
                onClick={isSubscriberWithAllowance ? redeem : startPaidCheckout}
                disabled={loading}
                data-testid={`${testid}-btn`}
                style={{
                  background: '#2563EB', color: '#fff', border: 'none',
                  padding: '12px 22px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? 'Starting…'
                  : isSubscriberWithAllowance ? 'Redeem your QS Review Call — £0 →'
                  : 'Book your call — £49 →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QSCallCTA;
